// What the device does about whose progress it holds, without asking anyone. Signing in claims the
// device's progress into the account and keeps syncing through the account's routes. Another
// account's local data on a shared device is set aside under that account's id (never merged, never
// dropped) and given back when it signs in again. Signing out sends what is queued first, then sets
// the data aside, so the signed-out Academy is empty and nothing a person wrote is lost.
import { getAuthState, onBeforeSignOut, onSessionChange } from "../social/auth";
import { hasAssetsWithPrefix, moveAssets, readAsset, removeAssetsWithPrefix } from "../assetCache";
import { POLICY } from "../dataCache";
import { LIVE_PREFIX, parkedPrefix, readBinding, writeBinding } from "./accountBinding";
import { claimProgress } from "./api";
import { isRecord } from "./guards";
import {
  adoptServerProgress,
  getLearnState,
  hydrateLearn,
  recordItem,
  resetLearnStore,
  settleLearn,
} from "./store";

async function settleStores(): Promise<void> {
  const [depth, wines] = await Promise.all([import("./depth/store"), import("./wines/store")]);
  await Promise.all([settleLearn(), depth.settleDepth(), wines.settleWines()]);
}

async function reloadStores(): Promise<void> {
  const [depth, wines] = await Promise.all([import("./depth/store"), import("./wines/store")]);
  resetLearnStore();
  depth.resetDepthStore();
  wines.resetWineStore();
  await Promise.all([hydrateLearn(), depth.hydrateDepth(), wines.hydrateWines()]);
}

/** Sets this device's Academy data aside under `ownerId`; the live Academy is then empty. */
async function park(ownerId: string): Promise<void> {
  await settleStores();
  // What was parked for this person before is older than what is live now.
  await removeAssetsWithPrefix(parkedPrefix(ownerId));
  await moveAssets(LIVE_PREFIX, parkedPrefix(ownerId) + LIVE_PREFIX);
  await reloadStores();
}

const anything = (value: unknown): value is unknown => value !== undefined;

/** Gives a person back what was set aside for them: moved whole into an empty Academy, merged item by item into one that has progress. */
async function giveBack(ownerId: string): Promise<void> {
  const prefix = parkedPrefix(ownerId);
  if (!(await hasAssetsWithPrefix(prefix))) return;
  await hydrateLearn();
  if (getLearnState().items.size === 0 && getLearnState().queue.size === 0) {
    await moveAssets(prefix + LIVE_PREFIX, LIVE_PREFIX);
    await reloadStores();
    return;
  }
  // A signed-out session produced progress meanwhile: the set-aside items join it, last writer wins.
  const version = POLICY.user.version;
  for (const key of ["progress", "queue"]) {
    const saved = await readAsset(`${prefix}${LIVE_PREFIX}${key}`, version, anything);
    const items = isRecord(saved) && Array.isArray(saved["items"]) ? saved["items"] : [];
    for (const item of items as unknown[]) {
      if (
        isRecord(item) &&
        typeof item["key"] === "string" &&
        typeof item["updatedAt"] === "string"
      ) {
        recordItem({ key: item["key"], updatedAt: item["updatedAt"], value: item["value"] });
      }
    }
  }
}

let settling: Promise<void> | undefined;
let again = false;

/** Brings the device's Academy in line with who is signed in. Safe to call from any trigger. */
export function settleAccount(): Promise<void> {
  if (settling !== undefined) {
    again = true;
    return settling;
  }
  const run = (async () => {
    const auth = getAuthState();
    await hydrateLearn();
    const binding = readBinding();
    if (auth.status === "signedOut") {
      // Nobody is signed in: whoever's progress this is waits aside, and the Academy shows none.
      if (binding !== undefined) {
        await park(binding.ownerId);
        writeBinding(undefined);
      }
      return;
    }
    if (auth.status !== "signedIn") return;
    const me = auth.user.id;
    if (binding !== undefined && binding.ownerId !== me) {
      await park(binding.ownerId);
      writeBinding(undefined);
    }
    if (readBinding()?.ownerId !== me) {
      await giveBack(me);
      writeBinding({ ownerId: me, claimed: false });
    }
    if (readBinding()?.claimed !== true) {
      try {
        const claimed = await claimProgress();
        adoptServerProgress({ items: claimed.items, servedAt: claimed.servedAt });
        writeBinding({ ownerId: me, claimed: true });
        const [{ syncNow }, depth] = await Promise.all([import("./sync"), import("./depth/sync")]);
        void syncNow();
        void depth.syncDepth();
      } catch {
        // Offline or unreachable: the device syncs per device until the next trigger tries again.
      }
    }
  })().finally(() => {
    settling = undefined;
    if (again) {
      again = false;
      void settleAccount();
    }
  });
  settling = run;
  return run;
}

/**
 * Starts the binding: settle now and whenever who is signed in changes or the connection returns, and
 * send what is queued to the account before a sign-out ends the session.
 */
export function startAccountBinding(): () => void {
  const stopBefore = onBeforeSignOut(async () => {
    if (readBinding()?.claimed !== true || getLearnState().queue.size === 0) return;
    const { syncNow } = await import("./sync");
    await syncNow().catch(() => undefined);
  });
  const stopSession = onSessionChange(() => {
    void settleAccount();
  });
  const onOnline = (): void => {
    void settleAccount();
  };
  window.addEventListener("online", onOnline);
  void settleAccount();
  return () => {
    stopBefore();
    stopSession();
    window.removeEventListener("online", onOnline);
  };
}
